import React from 'react'
import { DateTime } from 'luxon'
import { FunnelIcon } from '@heroicons/react/24/outline'
import { useLeamData } from 'hooks/useLeamData'
import { useDateRanges } from 'hooks/useDateRanges'
import { useWorkdaySummary } from 'hooks/useWorkdaySummary'
import { useAuth } from 'auth/AuthContext'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import ButtonComponent from 'components/common/ButtonComponent'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import AgroSection from './AgroSection'
import DataSection from './DataSection'
import WorkersSection from './WorkersSection'
import { getDateRangeSummary } from 'utils/dateRangeFilters'

const Leam = () => {
  const { user } = useAuth()
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [showDateRangeModal, setShowDateRangeModal] = React.useState(false)
  const { leamData } = useLeamData(dateRange)
  const dates = useDateRanges()
  const { data: workdayData } = useWorkdaySummary()

  if (leamData.loading) {
    return (
      <div className='p-6 bg-gray-50 min-h-screen'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-gray-600'>Loading LEAM summary...</div>
        </div>
      </div>
    )
  }

  if (leamData.error) {
    return (
      <div className='p-6 bg-gray-50 min-h-screen'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-red-600'>{leamData.error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className='p-3 bg-gray-50 min-h-screen'>
      <div className='flex justify-between items-start mb-2'>
        <div>
          <h1 className='text-xl font-semibold text-green-600'>LEAM Dashboard</h1>
          <p className='text-sm text-gray-600'>Filters ({getDateRangeSummary(dateRange)})</p>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-700'>{getDateRangeSummary(dateRange)}</span>
          <ButtonComponent className='flex items-center gap-2' onClick={() => setShowDateRangeModal(true)}>
            <FunnelIcon className='w-5 h-5' />
            <span>Advanced filters</span>
          </ButtonComponent>
        </div>
      </div>

      <hr className='border-t border-gray-300 mb-3' />

      <AgroSection data={leamData} dates={dates} />

      <DataSection
        data={leamData}
        dates={dates}
        type='picking'
        title='Resumen picking'
        color='blue'
      />

      <DataSection
        data={leamData}
        dates={dates}
        type='packing'
        title='Resumen packing'
        color='purple'
      />

      <WorkersSection
        leamData={leamData}
        workdayData={workdayData}
        previousDate={dates.previousDate}
      />
      <DateRangeFilterModal
        isOpen={showDateRangeModal}
        dateRange={dateRange}
        sectors={filterOptions.sectors}
        groups={filterOptions.groups}
        maxDate={currentDate}
        onClose={() => setShowDateRangeModal(false)}
        onApply={(nextRange) => {
          setDateRange(nextRange)
          setShowDateRangeModal(false)
        }}
      />
    </div>
  )
}

export default Leam
